import React from 'react';
import './DataTable.css';

/**
 * Definición de columna desacoplada del dominio.
 * El consumidor inyecta header/cell; DataTable solo presenta.
 */
export type DataTableColumn<T> = {
  id: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  /** Etiqueta para layout móvil (attr data-label) */
  label?: string;
};

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  data: T[];
  getRowId: (row: T) => string | number;
  loading?: boolean;
  emptyMessage?: string;
  emptyDescription?: string;
  /** Slot de acciones por fila (DIP: la página decide qué botones mostrar) */
  renderActions?: (row: T) => React.ReactNode;
  actionsHeader?: React.ReactNode;
  /** Slot superior (búsqueda, filtros, etc.) */
  toolbar?: React.ReactNode;
  /**
   * Barra de contexto entre toolbar y filas (conteo, filtros activos, etc.).
   * Técnica UI/UX: feedback / transparencia del estado de la lista.
   */
  summary?: React.ReactNode;
  /** Slot inferior (paginación, etc.) */
  footer?: React.ReactNode;
  className?: string;
  /** Filas skeleton al cargar */
  skeletonRows?: number;
  /** Etiqueta accesible de la tabla */
  'aria-label'?: string;
};

function DataTable<T>({
  columns,
  data,
  getRowId,
  loading = false,
  emptyMessage = 'Sin resultados',
  emptyDescription,
  renderActions,
  actionsHeader = 'Acciones',
  toolbar,
  summary,
  footer,
  className = '',
  skeletonRows = 5,
  'aria-label': ariaLabel,
}: DataTableProps<T>) {
  const hasActions = Boolean(renderActions);
  const colCount = columns.length + (hasActions ? 1 : 0);

  return (
    <div className={`data-table ${className}`.trim()}>
      <div className="data-table__surface">
        {toolbar ? <div className="data-table__toolbar">{toolbar}</div> : null}
        {summary ? (
          <div className="data-table__summary" role="status" aria-live="polite">
            {summary}
          </div>
        ) : null}

        <div className="data-table__scroll">
          <table className="data-table__table" aria-label={ariaLabel}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className={col.className}
                    scope="col"
                    style={{
                      width: col.width,
                      textAlign: col.align || 'left',
                    }}
                  >
                    {col.header}
                  </th>
                ))}
                {hasActions ? (
                  <th className="data-table__actions-head" scope="col" style={{ textAlign: 'right' }}>
                    {actionsHeader}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: skeletonRows }).map((_, i) => (
                  <tr key={`sk-${i}`} className="data-table__skeleton-row" aria-hidden>
                    {columns.map((col) => (
                      <td key={col.id}>
                        <span
                          className="data-table__skeleton-bar"
                          style={{ width: `${48 + ((i * 17 + col.id.length * 11) % 40)}%` }}
                        />
                      </td>
                    ))}
                    {hasActions ? (
                      <td>
                        <span className="data-table__skeleton-bar data-table__skeleton-bar--sm" />
                      </td>
                    ) : null}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="data-table__empty">
                    <div className="data-table__empty-inner">
                      <span className="data-table__empty-icon" aria-hidden />
                      <span className="data-table__empty-title">{emptyMessage}</span>
                      {emptyDescription ? (
                        <span className="data-table__empty-desc">{emptyDescription}</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={getRowId(row)} className="data-table__row">
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        data-label={col.label ?? (typeof col.header === 'string' ? col.header : col.id)}
                        className={col.className}
                        style={{ textAlign: col.align || 'left' }}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                    {hasActions && renderActions ? (
                      <td className="data-table__actions" data-label="Acciones">
                        <div className="data-table__actions-inner">{renderActions(row)}</div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {footer ? <div className="data-table__footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export default DataTable;
